import React from "react";
import { Box, Paper, Typography, Button, IconButton, Tooltip, TextField, Chip, ToggleButton, ToggleButtonGroup, Checkbox, FormControlLabel } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import globals from "../../Globals";
import { replaceDeep } from "../../Layout/Router";
import { PageContainer, SectionHeader, DataTable, StatCard, FormDialog, formatDate, relTime } from "../../Layout/Common";
import { FIXED_TABLE } from "../Localization/loc";
import clientKeys, { PLATFORMS, ANDROID_QUOTA, ANDROID_ALERT, isMasked, mask, isToken } from "./api";

// Client keys (Docs/ClientKeys.md §5, service / admin group `clientkeys`): the SDK keys the backend hands to a verified
// app, per platform - the providers table (add / edit / remove a key or a provider), the keysVersion and Rotate, the last
// 7 days of answers and the last 50 log rows. `#clientkeys` views (values come masked), `clientkeys` edits.
// URL: /<game>/clientkeys[/<platform>].
const canEdit = () => {
    if (globals.loginData?.titles?.isMaster) return true;
    const raw = globals.loginData?.titles?.roles?.[globals.selectedTitle];
    const roles = Array.isArray(raw) ? raw : raw && typeof raw === "object" ? Object.keys(raw) : [];
    return roles.includes("clientkeys");
};
const platformLabel = (p) => PLATFORMS.find(x => x.value === p)?.label ?? p;
const utcToday = () => new Date().toISOString().slice(0, 10);
const n0 = (v) => (v ?? 0).toLocaleString();
// One-line cells: a long value ends in an ellipsis instead of widening the column (the full text is in the title)
const CELL = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const resultColor = { ok: "success", denied: "warning", error: "error" };

export default class ClientKeysPage extends React.Component {
    state = {
        platform: PLATFORMS.some(p => p.value === globals.route?.deep?.[0]) ? globals.route.deep[0] : "android",
        row: null, rowLoaded: false, stats: [], log: [], busy: false,
        revealed: {},                                                     // "provider/key" → true
        editor: null,                                                     // { mode: "add"|"edit", provider, key, value }
        removing: null,                                                   // { provider, key, wholeProvider }
        rotating: false,
    };

    componentDidMount() { this.loadRow(); this.loadStats(); this.loadLog(); }

    setPlatform = (platform) => {
        if (!platform || platform === this.state.platform) return;
        this.setState({ platform, row: null, rowLoaded: false, revealed: {} }, this.loadRow);
        replaceDeep([platform]);
    }

    loadRow = () => {
        const platform = this.state.platform;
        clientKeys.get(platform)
            .then(row => { if (this.state.platform === platform) this.setState({ row, rowLoaded: true }); })
            .catch(() => { if (this.state.platform === platform) this.setState({ row: null, rowLoaded: true }); });
    }
    loadStats = () => clientKeys.stats(7).then(stats => this.setState({ stats })).catch(() => this.setState({ stats: [] }));
    loadLog = () => clientKeys.log(50).then(log => this.setState({ log })).catch(() => this.setState({ log: [] }));
    refresh = () => { this.loadRow(); this.loadStats(); this.loadLog(); }

    // A mutation: the answer's row when it carries one, else a reload
    mutate = (promise) => {
        this.setState({ busy: true });
        return promise
            .then(row => { if (row) this.setState({ row, rowLoaded: true }); else this.loadRow(); return true; })
            .catch(() => false)
            .finally(() => this.setState({ busy: false }));
    }

    // ---- providers table ----
    keyRows = () => {
        const providers = this.state.row?.providers ?? {};
        const rows = [];
        for (const provider of Object.keys(providers).sort()) {
            const keys = Object.keys(providers[provider]).sort();
            if (keys.length === 0) rows.push({ provider, key: null, value: "" });
            for (const key of keys) rows.push({ provider, key, value: providers[provider][key] });
        }
        return rows;
    }

    toggleReveal = (id) => this.setState(s => ({ revealed: { ...s.revealed, [id]: !s.revealed[id] } }));

    openAdd = (provider) => this.setState({ editor: { mode: "add", provider: provider ?? "", key: "", value: "" } });
    openEdit = (r) => this.setState({ editor: { mode: "edit", provider: r.provider, key: r.key, value: isMasked(r.value) ? "" : r.value } });
    saveEditor = () => {
        const { provider, key, value } = this.state.editor;
        this.mutate(clientKeys.set(this.state.platform, provider.trim(), key.trim(), value.trim()))
            .then(ok => { if (ok) this.setState({ editor: null }); });
    }

    confirmRemove = () => {
        const { provider, key, wholeProvider } = this.state.removing;
        this.mutate(clientKeys.unset(this.state.platform, provider, wholeProvider || !key ? null : key))
            .then(ok => { if (ok) this.setState({ removing: null }); });
    }

    confirmRotate = () => {
        this.mutate(clientKeys.rotate(this.state.platform)).then(ok => { if (ok) this.setState({ rotating: false }); });
    }

    renderProviders(edit) {
        const { row, rowLoaded, revealed, busy, platform } = this.state;
        const updated = row?.updatedDate
            ? `${relTime(row.updatedDate)}${row.updatedBy ? " · " + row.updatedBy : ""}`
            : "";
        const columns = [
            { id: "provider", label: "Provider", width: "18%", sx: CELL, render: r => <span title={r.provider}>{r.provider}</span> },
            { id: "key", label: "Key", width: "22%", sx: CELL,
                render: r => r.key == null ? <Typography variant="body2" color="text.secondary">(no keys)</Typography> : <span title={r.key}>{r.key}</span> },
            { id: "value", label: "Value", width: "40%", sx: CELL, render: r => {
                if (r.key == null) return "";
                const id = r.provider + "/" + r.key;
                const masked = isMasked(r.value);
                const shown = revealed[id] && !masked ? r.value : mask(r.value);
                return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                        <Tooltip title={masked ? "Masked by the backend (view only)" : revealed[id] ? "Hide" : "Reveal"}>
                            <span>
                                <IconButton size="small" disabled={masked} onClick={() => this.toggleReveal(id)}>
                                    {revealed[id] && !masked ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Box component="span" sx={{ ...CELL, fontFamily: "monospace", minWidth: 0 }} title={revealed[id] && !masked ? r.value : undefined}>{shown}</Box>
                    </Box>
                );
            }},
            { id: "updated", label: "Updated", width: "20%", sx: CELL, render: r => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                    <Box component="span" sx={{ ...CELL, flex: 1, minWidth: 0 }} title={row?.updatedDate ? formatDate(row.updatedDate) : undefined}>{updated}</Box>
                    {edit && r.key != null && (
                        <Tooltip title="Edit"><span><IconButton size="small" disabled={busy} onClick={() => this.openEdit(r)}><EditIcon fontSize="small" /></IconButton></span></Tooltip>
                    )}
                    {edit && r.key == null && (
                        <Tooltip title="Add a key to this provider"><span><IconButton size="small" disabled={busy} onClick={() => this.openAdd(r.provider)}><AddIcon fontSize="small" /></IconButton></span></Tooltip>
                    )}
                    {edit && (
                        <Tooltip title="Remove"><span><IconButton size="small" disabled={busy} onClick={() => this.setState({ removing: { provider: r.provider, key: r.key, wholeProvider: r.key == null } })}><DeleteIcon fontSize="small" /></IconButton></span></Tooltip>
                    )}
                </Box>
            )},
        ];
        const rows = this.keyRows();
        return (
            <Paper sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
                    <Typography variant="h6" sx={{ flex: 1 }}>{platformLabel(platform)} keys</Typography>
                    <Typography variant="body2">keysVersion <b>{row?.keysVersion ?? "—"}</b></Typography>
                    {edit && <Button size="small" variant="outlined" color="warning" disabled={busy || !row?.exists} onClick={() => this.setState({ rotating: true })}>Rotate</Button>}
                    {edit && <Button size="small" variant="contained" startIcon={<AddIcon />} disabled={busy} onClick={() => this.openAdd()}>Add key</Button>}
                </Box>
                <DataTable sx={FIXED_TABLE} columns={columns} rows={rows} getRowKey={r => r.provider + "/" + (r.key ?? "")} />
                {rows.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {rowLoaded ? (row?.exists ? "No providers on this platform." : "No keys for this platform (or the backend did not answer).") : "Loading…"}
                    </Typography>
                )}
            </Paper>
        );
    }

    // ---- stats ----
    renderStats() {
        const stats = [...this.state.stats].sort((a, b) => b.date.localeCompare(a.date) || a.platform.localeCompare(b.platform));
        const today = utcToday();
        const androidToday = stats.filter(d => d.platform === "android" && d.date === today).reduce((a, d) => a + d.requests, 0);
        const sum = (platform, f) => stats.filter(d => d.platform === platform).reduce((a, d) => a + f(d), 0);
        const columns = [
            { id: "date", label: "Day (UTC)", width: "14%", sx: CELL },
            { id: "platform", label: "Platform", width: "10%", sx: CELL, render: d => platformLabel(d.platform) },
            { id: "requests", label: "Requests", width: "12%", align: "right", sx: CELL, render: d => n0(d.requests) },
            { id: "ok", label: "OK", width: "10%", align: "right", sx: CELL, render: d => n0(d.ok) },
            { id: "deniedTotal", label: "Denied", width: "10%", align: "right", sx: CELL, render: d => n0(d.deniedTotal) },
            { id: "reasons", label: "Denied by reason", width: "34%", sx: CELL, render: d => {
                const text = Object.entries(d.denied).sort((a, b) => b[1] - a[1]).map(([k, v]) => `${k} ${n0(v)}`).join(" · ");
                return <span title={text}>{text}</span>;
            }},
            { id: "error", label: "Error", width: "10%", align: "right", sx: CELL, render: d => n0(d.error) },
        ];
        return (
            <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Last 7 days</Typography>
                <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                    <StatCard label="Android requests today" value={`${n0(androidToday)} / ${n0(ANDROID_QUOTA)}`}
                        secondary="Play Integrity decodes (UTC day)"
                        tertiary={androidToday > ANDROID_ALERT ? `above the ${n0(ANDROID_ALERT)} alert` : undefined} tertiaryColor="error.main" />
                    <StatCard label="Android ok / denied / error" value={`${n0(sum("android", d => d.ok))} / ${n0(sum("android", d => d.deniedTotal))} / ${n0(sum("android", d => d.error))}`} secondary="7 days" />
                    <StatCard label="iOS ok / denied / error" value={`${n0(sum("ios", d => d.ok))} / ${n0(sum("ios", d => d.deniedTotal))} / ${n0(sum("ios", d => d.error))}`} secondary="7 days" />
                </Box>
                {androidToday > ANDROID_ALERT && (
                    <Typography variant="body2" color="error.main" sx={{ mb: 1 }}>
                        Today's Android requests are above {n0(ANDROID_ALERT)} of the {n0(ANDROID_QUOTA)} daily Play Integrity quota.
                    </Typography>
                )}
                <DataTable sx={FIXED_TABLE} columns={columns} rows={stats} getRowKey={d => d.date + "/" + d.platform} />
                {stats.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>No answers in the last 7 days.</Typography>}
            </Paper>
        );
    }

    // ---- log ----
    renderLog() {
        const log = this.state.log;
        const hasIp = log.some(r => r.ip);
        const base = [
            { id: "date", label: "Date", w: 16, render: r => <span title={formatDate(r.date)}>{formatDate(r.date)}</span> },
            { id: "platform", label: "Platform", w: 8, render: r => platformLabel(r.platform) },
            { id: "result", label: "Result", w: 8, render: r => r.result ? <Chip size="small" label={r.result} color={resultColor[r.result] ?? "default"} /> : "" },
            { id: "reason", label: "Reason", w: 14, render: r => <span title={r.reason}>{r.reason}</span> },
            { id: "app", label: "App", w: 12, render: r => <span title={r.app}>{r.app}</span> },
            { id: "device", label: "Device", w: 14, render: r => {
                const t = [r.device, r.licensing].filter(Boolean).join(" · ");
                return <span title={t}>{r.device}</span>;
            }},
            { id: "versionCode", label: "versionCode", w: 10, render: r => <span title={r.appVersion ? `app ${r.appVersion}` : undefined}>{r.versionCode}</span> },
            { id: "country", label: "Country", w: 8 },
            ...(hasIp ? [{ id: "ip", label: "IP", w: 10, render: r => <span title={r.ip ?? ""}>{r.ip ?? ""}</span> }] : []),
        ];
        // Explicit widths always; without the ip column the others keep their proportions and still add to 100 %
        const total = base.reduce((a, c) => a + c.w, 0);
        const columns = base.map(({ w, ...c }) => ({ ...c, width: `${((w * 100) / total).toFixed(2)}%`, sx: CELL }));
        return (
            <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Last 50 requests</Typography>
                <DataTable sx={FIXED_TABLE} columns={columns} rows={log} getRowKey={(r, i) => i} />
                {log.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>No requests logged.</Typography>}
            </Paper>
        );
    }

    // ---- dialogs ----
    renderEditor() {
        const e = this.state.editor;
        if (!e) return null;
        const set = (patch) => this.setState({ editor: { ...e, ...patch } });
        const providerOk = isToken(e.provider.trim()), keyOk = isToken(e.key.trim()), valueOk = isToken(e.value.trim());
        const exists = e.mode === "add" && this.state.row?.providers?.[e.provider.trim()]?.[e.key.trim()] != null;
        return (
            <FormDialog open title={e.mode === "add" ? `Add a key (${platformLabel(this.state.platform)})` : `Edit ${e.provider} / ${e.key}`}
                onClose={() => this.setState({ editor: null })} onConfirm={this.saveEditor} confirmText="Save"
                confirmDisabled={this.state.busy || !providerOk || !keyOk || !valueOk} maxWidth="sm" fullWidth>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                    <TextField label="Provider" size="small" value={e.provider} disabled={e.mode === "edit"} autoFocus={e.mode === "add" && !e.provider}
                        onChange={ev => set({ provider: ev.target.value })} helperText="e.g. meta, firebase (no spaces)"
                        error={e.provider.length > 0 && !providerOk} />
                    <TextField label="Key" size="small" value={e.key} disabled={e.mode === "edit"} autoFocus={e.mode === "add" && !!e.provider}
                        onChange={ev => set({ key: ev.target.value })} helperText={exists ? "This key exists: saving replaces its value" : "e.g. appId, clientToken (no spaces)"}
                        error={e.key.length > 0 && !keyOk} />
                    <TextField label="Value" size="small" value={e.value} autoFocus={e.mode === "edit"} onChange={ev => set({ value: ev.target.value })}
                        helperText="The value has no spaces. Saving keeps the keysVersion; Rotate makes clients refresh."
                        error={e.value.length > 0 && !valueOk} InputProps={{ sx: { fontFamily: "monospace" } }} />
                </Box>
            </FormDialog>
        );
    }

    renderRemove() {
        const r = this.state.removing;
        if (!r) return null;
        const keyCount = Object.keys(this.state.row?.providers?.[r.provider] ?? {}).length;
        const whole = r.wholeProvider || r.key == null;
        return (
            <FormDialog open title="Remove" onClose={() => this.setState({ removing: null })} onConfirm={this.confirmRemove}
                confirmText="Remove" confirmDisabled={this.state.busy}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                    {whole
                        ? <>Remove the provider <b>{r.provider}</b> and its {keyCount} key{keyCount === 1 ? "" : "s"} from {platformLabel(this.state.platform)}?</>
                        : <>Remove <b>{r.provider} / {r.key}</b> from {platformLabel(this.state.platform)}?</>}
                </Typography>
                {r.key != null && (
                    <FormControlLabel control={<Checkbox checked={!!r.wholeProvider} onChange={ev => this.setState({ removing: { ...r, wholeProvider: ev.target.checked } })} />}
                        label={`Remove the whole provider ${r.provider} (${keyCount} key${keyCount === 1 ? "" : "s"})`} />
                )}
                <Typography variant="body2" color="text.secondary">Clients that cached the keys keep them until they validate again (Rotate forces that).</Typography>
            </FormDialog>
        );
    }

    renderRotate() {
        if (!this.state.rotating) return null;
        const v = this.state.row?.keysVersion;
        return (
            <FormDialog open title="Rotate the keys version" onClose={() => this.setState({ rotating: false })} onConfirm={this.confirmRotate}
                confirmText="Rotate" confirmDisabled={this.state.busy}>
                <Typography variant="body2">
                    Bump the {platformLabel(this.state.platform)} keysVersion{v != null ? <> from <b>{v}</b> to <b>{v + 1}</b></> : null}?
                    Every client refreshes its cached keys at its next validation.
                </Typography>
            </FormDialog>
        );
    }

    render() {
        const edit = canEdit();
        return (
            <PageContainer>
                <SectionHeader title="Client keys">
                    {!edit && <Chip size="small" label="view only" />}
                    <Tooltip title="Reload"><IconButton onClick={this.refresh}><RefreshIcon /></IconButton></Tooltip>
                </SectionHeader>
                <Box sx={{ mb: 2 }}>
                    <ToggleButtonGroup size="small" exclusive value={this.state.platform} onChange={(ev, v) => this.setPlatform(v)}>
                        {PLATFORMS.map(p => <ToggleButton key={p.value} value={p.value}>{p.label}</ToggleButton>)}
                    </ToggleButtonGroup>
                </Box>
                {this.renderProviders(edit)}
                {this.renderStats()}
                {this.renderLog()}
                {this.renderEditor()}
                {this.renderRemove()}
                {this.renderRotate()}
            </PageContainer>
        );
    }
}
